import React from 'react';
import { X, Calendar, Clock, Users, ArrowRight, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Brief Jul 6 2026 P1 — Aperçu participant depuis le wizard de création :
// simule la page publique /event/{id} avec les données du formulaire (non
// sauvegardées). Light theme volontaire = fidèle à ce que voit le visiteur.
const SessionPreviewModal = ({ isOpen, onClose, form, isGroup, forceFree }) => {
  const { user } = useAuth();
  if (!isOpen) return null;

  const start = form.start_local ? new Date(form.start_local) : null;
  let duration = form.duration_minutes || 60;
  if (form.duration_mode === 'custom' && form.end_local && start) {
    duration = Math.max(15, Math.round((new Date(form.end_local) - start) / 60000));
  }
  const priceNum = forceFree === false ? Math.max(0, Number(form.price) || 0) : 0;
  const isPaid = priceNum > 0;
  const when = start && !Number.isNaN(start.getTime())
    ? start.toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })
    : 'Date à définir';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="session-preview-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-slate-50 shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2.5 bg-[#0d1f35] text-cyan-200 text-xs font-medium">
          <span className="inline-flex items-center gap-1.5">
            <Eye className="h-3.5 w-3.5" /> Aperçu — ce que verront les participants
          </span>
          <button
            type="button"
            onClick={onClose}
            data-testid="session-preview-close"
            className="p-1 rounded hover:bg-white/10 text-gray-300"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-6">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="session-preview-title">
            {form.title || 'Titre de ta session'}
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-3 mb-5 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1.5" data-testid="session-preview-when">
              <Calendar className="h-4 w-4" /> {when}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4" /> {duration} min
            </span>
            {isGroup && (
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-4 w-4" /> 0 inscrit / {form.max_capacity}
              </span>
            )}
          </div>
          {form.description && (
            <p className="text-sm text-gray-700 whitespace-pre-line mb-6">{form.description}</p>
          )}
          <div className="flex items-center gap-3 bg-white rounded-xl p-4 border border-gray-200 mb-6" data-testid="session-preview-expert-card">
            {user?.avatar ? (
              <img src={user.avatar} alt="" className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-pink-500 text-white flex items-center justify-center font-semibold">
                {(user?.display_name || '?')[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 truncate">{user?.display_name || 'Toi'}</p>
              {user?.tagline && <p className="text-sm text-gray-500 truncate">{user.tagline}</p>}
            </div>
          </div>
          {isPaid ? (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-5 text-center" data-testid="session-preview-price-paid">
              <p className="text-sm text-purple-700 font-semibold">{priceNum} CHF · Paiement sécurisé via Stripe</p>
              <p className="text-[11px] text-purple-500/80 mt-1">Tu reçois le paiement directement sur ton compte.</p>
            </div>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-5 text-center" data-testid="session-preview-price-free">
              <p className="text-sm text-emerald-700 font-semibold">
                {forceFree === false ? 'Session gratuite' : 'Gratuit pendant la beta'}
              </p>
            </div>
          )}
          <button
            type="button"
            disabled
            className="w-full inline-flex items-center justify-center gap-2 font-semibold rounded-xl px-6 py-3 text-white opacity-90 cursor-default"
            style={{ background: form.visibility === 'private' ? '#7F77DD' : '#1D9E75' }}
            data-testid="session-preview-cta"
          >
            {isPaid ? `Payer ${priceNum} CHF →` : 'Réserver cette session'} <ArrowRight className="h-4 w-4" />
          </button>
          <p className="text-center text-[11px] text-gray-400 mt-3">
            Aperçu non interactif — publie ta session pour obtenir le lien partageable.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SessionPreviewModal;
